import React from 'react';
import useScrollLock from '../hooks/useScrollLock';
import { createPortal } from 'react-dom';

interface TermsOfServiceModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const TermsOfServiceModal: React.FC<TermsOfServiceModalProps> = ({ isOpen, onClose }) => {
    // Prevent scrolling when modal is open
    useScrollLock(isOpen);

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Modal Content */}
            <div className="relative bg-white/90 backdrop-blur-md rounded-3xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl border border-orange-200/50 animate-in fade-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="flex justify-between items-center p-6 sm:p-8 border-b border-orange-200/30 shrink-0">
                    <div>
                        <h2 className="text-3xl font-bold text-gray-900">Terms of Service</h2>
                        <p className="text-gray-500 text-sm mt-1">Last updated: March 1, 2026</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-900 text-2xl font-bold transition-colors w-10 h-10 flex items-center justify-center rounded-full hover:bg-black/5"
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                {/* Scrollable Body */}
                <div className="overflow-y-auto p-6 sm:p-8 space-y-8">
                    <section>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">1. Acceptance of Terms</h3>
                        <div className="space-y-3 text-gray-600">
                            <p>By accessing and using Focuzon, you accept and agree to be bound by the terms and provision of this agreement.</p>
                        </div>
                    </section>

                    <section>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">2. Description of Service</h3>
                        <div className="space-y-3 text-gray-600">
                            <p>Focuzon provides a digital workspace designed for focus and productivity. The Service includes:</p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>Focus timers and session management</li>
                                <li>Task tracking and analytics</li>
                                <li>Ambient soundscapes and music</li>
                                <li>Collaboration features for teams</li>
                            </ul>
                        </div>
                    </section>

                    <section>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">3. User Conduct</h3>
                        <div className="space-y-3 text-gray-600">
                            <p>You agree to use Focuzon only for lawful purposes. You are prohibited from:</p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>Using the service for any illegal or unauthorized purpose</li>
                                <li>Attempting to hack, destabilize, or adapt the Service</li>
                                <li>Transmitting any worms, viruses, or destructive code</li>
                                <li>Sharing your account credentials with others</li>
                            </ul>
                        </div>
                    </section>

                    <section>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">4. Intellectual Property</h3>
                        <div className="space-y-3 text-gray-600">
                            <p>The Service and its original content, features, and functionality are owned by Focuzon and are protected by international copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.</p>
                        </div>
                    </section>

                    <section>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">5. Termination</h3>
                        <div className="space-y-3 text-gray-600">
                            <p>We may terminate or suspend access to our Service immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.</p>
                        </div>
                    </section>

                    <section>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">6. Limitation of Liability</h3>
                        <div className="space-y-3 text-gray-600">
                            <p>In no event shall Focuzon, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of or inability to access or use the Service.</p>
                        </div>
                    </section>

                    <section>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">7. Changes to Terms</h3>
                        <div className="space-y-3 text-gray-600">
                            <p>We reserve the right, at our sole discretion, to modify or replace these Terms at any time. We will try to provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.</p>
                        </div>
                    </section>

                    <section>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">8. Contact Us</h3>
                        <div className="space-y-3 text-gray-600">
                            <p>If you have any questions about these Terms, please contact us:</p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>Email: support@focuzon.com</li>
                            </ul>
                        </div>
                    </section>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-orange-200/30 bg-orange-50/50 rounded-b-3xl">
                    <p className="text-center text-gray-500 text-sm">
                        By using our services, you agree to these Terms of Service.
                    </p>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default TermsOfServiceModal;
